<script setup>
import { computed, h, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

import SettingsPageLayout from 'dashboard/components-next/ailab/SettingsPageLayout.vue';
import SettingsHeader from 'dashboard/components-next/ailab/pageComponents/settings/SettingsHeader.vue';
import SuggestedScenarios from 'dashboard/components-next/ailab/assistant/SuggestedRules.vue';
import ScenariosCard from 'dashboard/components-next/ailab/assistant/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/ailab/assistant/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/ailab/assistant/AddNewScenariosDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { formatMessage } = useMessageFormatter();
const assistantId = route.params.assistantId;

const uiFlags = useMapGetter('ailabScenarios/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const assistant = computed(() =>
  store.getters['ailabAssistants/getRecord'](Number(assistantId))
);
const scenarios = useMapGetter('ailabScenarios/getRecords');

const searchQuery = ref('');

const breadcrumbItems = computed(() => {
  return [
    {
      label: t('AILAB.ASSISTANTS.SETTINGS.BREADCRUMB.ASSISTANT'),
      routeName: 'ailab_assistants_index',
    },
    { label: assistant.value?.name, routeName: 'ailab_assistants_edit' },
    { label: t('AILAB.ASSISTANTS.SCENARIOS.BREADCRUMB.TITLE') },
  ];
});

const LINK_INSTRUCTION_CLASS =
  '[&_a[href^="tool://"]]:text-indigo-600 [&_a:not([href^="tool://"])]:text-slate-900 [&_a]:pointer-events-none [&_a]:cursor-default';

const renderInstruction = instruction => () =>
  h('span', {
    class: `text-sm text-slate-900 py-4 prose prose-sm min-w-0 break-words ${LINK_INSTRUCTION_CLASS}`,
    innerHTML: instruction,
  });

// Suggested example scenarios for quick add
const scenariosExample = [
  {
    id: 1,
    title: 'Prospective Buyer',
    description:
      'Handle customers who are showing interest in purchasing a license',
    instruction:
      'If someone is interested in purchasing a license, ask them for following:\n\n1. How many licenses are they willing to purchase?\n2. Are they migrating from another platform?\n. Once these details are collected, do the following steps\n1. add a private note to with the information you collected using [Add Private Note](tool://add_private_note)\n2. Add label "sales" to the contact using [Add Label to Conversation](tool://add_label_to_conversation)\n3. Reply saying "one of us will reach out soon" and provide an estimated timeline for the response and [Handoff to Human](tool://handoff)',
    tools: ['add_private_note', 'add_label_to_conversation', 'handoff'],
  },
];

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_scenarios_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('AILAB.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('AILAB.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('AILAB.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

const updateScenario = async scenario => {
  try {
    await store.dispatch('ailabScenarios/update', {
      id: scenario.id,
      assistantId: route.params.assistantId,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('AILAB.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('AILAB.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const deleteScenario = async id => {
  try {
    await store.dispatch('ailabScenarios/delete', {
      id,
      assistantId: route.params.assistantId,
    });
    useAlert(t('AILAB.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('AILAB.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

// TODO: Add bulk delete endpoint
const bulkDeleteScenarios = async ids => {
  const idsArray = ids || Array.from(bulkSelectedIds.value);
  await Promise.all(
    idsArray.map(id =>
      store.dispatch('ailabScenarios/delete', {
        id,
        assistantId: route.params.assistantId,
      })
    )
  );
  bulkSelectedIds.value = new Set();
  useAlert(t('AILAB.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
};

const addScenario = async scenario => {
  try {
    await store.dispatch('ailabScenarios/create', {
      assistantId: route.params.assistantId,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('AILAB.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('AILAB.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const addAllExampleScenarios = async () => {
  try {
    scenariosExample.forEach(async scenario => {
      await store.dispatch('ailabScenarios/create', {
        assistantId: route.params.assistantId,
        ...scenario,
      });
    });
    useAlert(t('AILAB.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('AILAB.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store.dispatch('ailabScenarios/get', {
    assistantId: assistantId,
  });
  store.dispatch('ailabTools/getTools');
});
</script>

<template>
  <SettingsPageLayout
    :breadcrumb-items="breadcrumbItems"
    :is-fetching="isFetching"
  >
    <template #body>
      <div class="flex flex-col gap-6">
        <SettingsHeader
          :heading="$t('AILAB.ASSISTANTS.SCENARIOS.TITLE')"
          :description="$t('AILAB.ASSISTANTS.SCENARIOS.DESCRIPTION')"
        />

        <div v-if="shouldShowSuggestedRules" class="flex flex-col gap-4">
          <SuggestedScenarios
            :title="$t('AILAB.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
            :items="scenariosExample"
            @close="closeSuggestedRules"
            @add="addAllExampleScenarios"
          >
            <template #default="{ item }">
              <div class="flex items-center gap-3 justify-between">
                <span class="text-sm text-slate-900 font-semibold">
                  {{ item.title }}
                </span>
                <Button
                  :label="
                    $t('AILAB.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.ADD_SINGLE')
                  "
                  variant="outline"
                  color="slate"
                  size="xs"
                  class="flex-shrink-0"
                  @click="addScenario(item)"
                />
              </div>
              <div class="flex flex-col">
                <span class="text-sm text-slate-600 mt-2">
                  {{ item.description }}
                </span>
                <component
                  :is="
                    renderInstruction(formatMessage(item.instruction, false))
                  "
                />
                <span class="text-sm text-slate-600 font-medium mb-1">
                  {{
                    t('AILAB.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TOOLS_USED')
                  }}
                  {{ item.tools?.map(tool => `@${tool}`).join(', ') }}
                </span>
              </div>
            </template>
          </SuggestedScenarios>
        </div>

        <div
          class="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col gap-4"
        >
          <div
            class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3"
          >
            <BulkSelectBar
              v-model="bulkSelectedIds"
              :all-items="scenarios"
              :select-all-label="buildSelectedCountLabel"
              :selected-count-label="selectedCountLabel"
              :delete-label="
                $t(
                  'AILAB.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON'
                )
              "
              @bulk-delete="bulkDeleteScenarios"
            >
              <template #default-actions>
                <AddNewScenariosDialog @add="addScenario" />
              </template>
            </BulkSelectBar>
            <div
              v-if="scenarios.length && bulkSelectedIds.size === 0"
              class="w-full sm:max-w-[22.5rem] min-w-0"
            >
              <Input
                v-model="searchQuery"
                :placeholder="
                  t('AILAB.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
                "
              />
            </div>
          </div>
          <div v-if="scenarios.length === 0" class="mt-1 mb-2">
            <span class="text-slate-600 text-sm">
              {{ t('AILAB.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE') }}
            </span>
          </div>
          <div v-else-if="filteredScenarios.length === 0" class="mt-1 mb-2">
            <span class="text-slate-600 text-sm">
              {{ t('AILAB.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
            </span>
          </div>
          <div v-else class="flex flex-col gap-2">
            <ScenariosCard
              v-for="scenario in filteredScenarios"
              :id="scenario.id"
              :key="scenario.id"
              :title="scenario.title"
              :description="scenario.description"
              :instruction="scenario.instruction"
              :tools="scenario.tools"
              :is-selected="bulkSelectedIds.has(scenario.id)"
              :selectable="
                hoveredCard === scenario.id || bulkSelectedIds.size > 0
              "
              @select="handleRuleSelect"
              @delete="deleteScenario(scenario.id)"
              @update="updateScenario"
              @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsPageLayout>
</template>
